import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Users, BarChart3, CheckCircle } from 'lucide-react';

const Services = () => {
  const { t } = useLanguage();

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <div className="absolute inset-0 bg-gradient-to-br from-white to-gray-50 dark:from-gray-900 dark:to-gray-800"></div>
          <div className="absolute top-20 left-10 w-64 h-64 bg-[#F05627] rounded-full opacity-10 blur-3xl animate-float"></div>
          <div className="absolute bottom-20 right-10 w-80 h-80 bg-blue-500 rounded-full opacity-10 blur-3xl animate-float" style={{ animationDelay: '2s' }}></div>
        </div>

        <div className="container mx-auto px-4 z-10 relative">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl md:text-6xl font-bold text-gray-900 dark:text-white mb-6 animate-fadeInUp">
              {t('services.title')}
            </h1>
          </div>
        </div>
      </section>

      {/* Qualitative Research Section */}
      <section className="py-20 bg-white dark:bg-gray-900">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="grid md:grid-cols-2 gap-12 items-start">
              <div className="sticky top-32 animate-fadeInLeft">
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-orange-50 rounded-full mb-6">
                  <Users className="w-5 h-5 text-[#F05627]" />
                  <span className="text-sm font-semibold text-[#F05627]">{t('home.qualitative.title')}</span>
                </div>
                <h2 className="text-4xl font-bold text-gray-900 dark:text-white mb-6">
                  {t('home.qualitative.subtitle')}
                </h2>
                <p className="text-lg text-gray-600 dark:text-gray-300 mb-8">
                  {t('home.qualitative.description')}
                </p>
                <div className="relative">
                  <div className="absolute inset-0 bg-gradient-to-br from-orange-100 to-orange-50 rounded-3xl transform rotate-3"></div>
                  <div className="relative bg-white rounded-3xl shadow-2xl p-8">
                    <Users className="w-16 h-16 text-[#F05627] mb-4" />
                    <div className="text-sm text-gray-600">Derinlemesine Analiz</div>
                  </div>
                </div>
              </div>

              <div className="space-y-8 animate-fadeInRight">
                <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-8 border-2 border-gray-100 dark:border-gray-700 hover:border-[#F05627] transition-colors">
                  <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">{t('home.qualitative.methods')}</h3>
                  <ul className="space-y-3">
                    {t('home.qualitative.methodList').map((method, index) => (
                      <li key={index} className="flex items-start gap-3">
                        <CheckCircle className="w-5 h-5 text-[#F05627] mt-0.5 flex-shrink-0" />
                        <span className="text-gray-700 dark:text-gray-300">{method}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-8 border-2 border-gray-100 dark:border-gray-700 hover:border-[#F05627] transition-colors">
                  <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">{t('home.qualitative.benefits')}</h3>
                  <ul className="space-y-3">
                    {t('home.qualitative.benefitList').map((benefit, index) => (
                      <li key={index} className="flex items-start gap-3">
                        <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                        <span className="text-gray-700 dark:text-gray-300">{benefit}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Quantitative Research Section */}
      <section className="py-20 bg-gray-50 dark:bg-gray-800">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="grid md:grid-cols-2 gap-12 items-start">
              <div className="order-2 md:order-1 space-y-8 animate-fadeInLeft">
                <div className="bg-white dark:bg-gray-700 rounded-2xl shadow-lg p-8 border-2 border-gray-100 dark:border-gray-600 hover:border-blue-600 transition-colors">
                  <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">{t('home.quantitative.methods')}</h3>
                  <ul className="space-y-3">
                    {t('home.quantitative.methodList').map((method, index) => (
                      <li key={index} className="flex items-start gap-3">
                        <CheckCircle className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                        <span className="text-gray-700 dark:text-gray-300">{method}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="bg-white dark:bg-gray-700 rounded-2xl shadow-lg p-8 border-2 border-gray-100 dark:border-gray-600 hover:border-blue-600 transition-colors">
                  <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">{t('home.quantitative.benefits')}</h3>
                  <ul className="space-y-3">
                    {t('home.quantitative.benefitList').map((benefit, index) => (
                      <li key={index} className="flex items-start gap-3">
                        <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                        <span className="text-gray-700 dark:text-gray-300">{benefit}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              <div className="order-1 md:order-2 sticky top-32 animate-fadeInRight">
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-50 rounded-full mb-6">
                  <BarChart3 className="w-5 h-5 text-blue-600" />
                  <span className="text-sm font-semibold text-blue-600">{t('home.quantitative.title')}</span>
                </div>
                <h2 className="text-4xl font-bold text-gray-900 dark:text-white mb-6">
                  {t('home.quantitative.subtitle')}
                </h2>
                <p className="text-lg text-gray-600 dark:text-gray-300 mb-8">
                  {t('home.quantitative.description')}
                </p>
                <div className="relative">
                  <div className="absolute inset-0 bg-gradient-to-br from-blue-100 to-blue-50 rounded-3xl transform -rotate-3"></div>
                  <div className="relative bg-white rounded-3xl shadow-2xl p-8">
                    <BarChart3 className="w-16 h-16 text-blue-600 mb-4" />
                    <div className="grid grid-cols-3 gap-4">
                      {[85, 92, 78].map((value, index) => (
                        <div key={index} className="text-center">
                          <div className="text-2xl font-bold text-blue-600">{value}%</div>
                          <div className="text-xs text-gray-600">Metric {index + 1}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Services;